import { useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { useController, useForm } from 'react-hook-form'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import { Button } from '@atoms'
import Badge from '@components/atoms/Badge'
import { RadioGroup } from '@components/atoms/RadioGroup'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/atoms/Tabs'
import Checkbox from '@components/atoms/form/Checkbox'
import { Textarea } from '@components/atoms/form/Textarea'
import { CopiableValue } from '@components/molecules/CopiableValue'
import { EventRsvpPicker, type EventRsvp } from '@components/molecules/EventRsvpPicker'
import Icon from '@components/utils/Icon'
import { toast } from '@stores/toastStore'
import { SupporterStatusBadge } from '@frontend/contexts/users_management/components/SupporterStatusBadge'

import {
  CallbackCard,
  SkipInSessionSwitch,
  callbackDueAt,
  CompleteCallbackField,
  ScheduleCallbackFields,
  callbackDraftError,
  callbackOwner,
  draftWhen,
  existingCallbackNotice,
  newCallbackDraft,
  ownersCallback,
  type AssignablePerson,
  type CallbackDraft,
} from '../components/ScheduleCallbackFields'
import { ContactsLog, type ContactEntry } from '../components/ContactsLog'
import {
  CALL_OUTCOMES,
  ContactOutcomeField,
  RadioGroupCard,
  SAMPLE_EVENTS,
  SUCCESSFUL_CONTACT_STATUSES,
  type ContactOutcome,
  type Outcome,
} from '../components/LogContactForm'

/**
 * Calling session supporter page v2, with callbacks (design-phase prototype)
 *
 * The version built on PR #7415's inline log-call form. For today's page with
 * just a "Callback requested" status added, see CallingSessionCallbackPreview.
 *
 * The calling screen as it looks after PR #7415 ("Log calls inline on the
 * calling screen"): the log-call form sits in the page, next to the supporter
 * summary card. This adds a "Callback requested" outcome. Picking it opens the
 * schedule-callback fields inside the form, so logging the call and booking the
 * callback is one save.
 *
 * Layout and copy follow #7415's CallingSessionSupporterShow, LogCallForm,
 * SupporterSummaryCard and CallInProgressBanner. Sample data only.
 */

// ---------- Sample data ----------

type Leader = { id: string; name: string }

const ME: Leader = { id: 'u1', name: 'Chiara Conti' }
const LEADERS: Leader[] = [ME, { id: 'u2', name: 'Marco Ferri' }, { id: 'u3', name: 'Sofia Greco' }]
// Who an admin can assign a callback to. Viewing as an admin, you are one.
const ASSIGNABLE: AssignablePerson[] = [
  { ...ME, kind: 'admin' },
  { id: 'u4', name: 'Alessandro Moretti', kind: 'admin' },
  ...LEADERS.filter((l) => l.id !== ME.id).map((l) => ({ ...l, kind: 'leader' as const })),
]
const GROUP = { name: 'Milano Centro' }
const ownerName = (draft: CallbackDraft) => ASSIGNABLE.find((p) => p.id === draft.assigneeId)?.name ?? 'Any leader'
const SESSION = { name: 'Tuesday evening calls', callList: 'October volunteer drive' }
const CYCLE = { number: 1, available: 141 }

const SUPPORTER = {
  firstName: 'Maria',
  lastName: 'Rossi',
  fullName: 'Maria Rossi',
  phone: '0412 345 678',
  email: 'maria.rossi@example.com',
  tags: ['Volunteer', 'Rally 2026'],
  lastContacted: '12 September 2026',
  supporterSince: '3 March 2024',
  location: 'Milano, MI, 20121',
  federalElectorate: 'Milano 1',
  note: 'Keen on door-knocking. Prefers calls after work.',
}

// Where this supporter sits in the session, for the footer pagination.
const POSITION = 3
const SESSION_SUPPORTERS = ['Luca Bianchi', 'Giulia Esposito', 'Maria Rossi', 'Paolo Romano', 'Elena Colombo']

const at = (daysFromToday: number, hour: number) => {
  const d = new Date()
  d.setDate(d.getDate() + daysFromToday)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

const INITIAL_CONTACTS: ContactEntry[] = [
  {
    id: 'c1',
    outcome: 'no_answer',
    at: at(-17, 18),
    by: 'Marco Ferri',
    note: '',
  },
  {
    id: 'c2',
    outcome: 'meaningful_interaction',
    at: at(-40, 19),
    by: ME.name,
    note: 'Signed up for the doorknock. Asked about lifts.',
  },
]

// ---------- Log call form (with the embedded callback) ----------

type FormData = {
  contactStatus: Outcome | ''
  note: string
  callCompleted: boolean
  contactOutcome: ContactOutcome | null
  completeCallback: boolean
  eventRsvps: EventRsvp[]
}

const defaultValues: FormData = {
  contactStatus: '',
  note: '',
  callCompleted: false,
  contactOutcome: null,
  completeCallback: false,
  eventRsvps: [],
}

type SavedCall = { outcome: Outcome; note: string; callback: CallbackDraft | null; completeCallback: boolean }

function LogCallForm({
  now,
  canAssign,
  initialOutcome,
  openCallback,
  onSave,
}: {
  now: Date
  canAssign: boolean
  initialOutcome: Outcome | ''
  openCallback: CallbackDraft | null
  onSave: (call: SavedCall) => void
}) {
  const headingId = useId()
  const callbackHeadingId = useId()
  const [callback, setCallback] = useState<CallbackDraft>(() =>
    openCallback
      ? {
          ...openCallback,
          assigneeId: callbackOwner(openCallback, ME.id) === 'other' ? ME.id : openCallback.assigneeId,
        }
      : newCallbackDraft(now, ME.id)
  )

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { ...defaultValues, contactStatus: initialOutcome } })

  const { field: outcomeField } = useController({
    name: 'contactStatus',
    control,
    rules: { required: 'Pick how the call went.' },
  })
  const { field: eventRsvpsField } = useController({ name: 'eventRsvps', control })
  const outcomeError = errors.contactStatus
  const callbackRequested = watch('contactStatus') === 'callback_requested'
  const callbackError = callbackRequested ? callbackDraftError(callback, now) : null

  const onSubmit = (data: FormData) => {
    if (!data.contactStatus || callbackError) return
    onSave({
      outcome: data.contactStatus,
      note: data.note,
      callback: callbackRequested ? callback : null,
      completeCallback: !!openCallback && !callbackRequested && data.completeCallback,
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 id={headingId} className="tw:mb-4 tw:text-2xl tw:font-semibold tw:text-gray-900">
        How did the call go?
      </h2>

      <RadioGroup
        aria-labelledby={headingId}
        aria-describedby={outcomeError ? 'contactStatus.error' : undefined}
        value={outcomeField.value}
        onValueChange={(value) => {
          outcomeField.onChange(value)
          setValue('callCompleted', SUCCESSFUL_CONTACT_STATUSES.includes(value as Outcome))
          setValue('completeCallback', SUCCESSFUL_CONTACT_STATUSES.includes(value as Outcome))
        }}
        onBlur={outcomeField.onBlur}
        name={outcomeField.name}
        className="tw:grid-cols-2 tw:gap-3 tw:sm:grid-cols-4"
      >
        {CALL_OUTCOMES.map(({ value, label, icon }) => (
          <RadioGroupCard key={value} value={value}>
            <Icon type={icon} className="tw:h-6 tw:w-6" />
            <span className="tw:text-sm tw:font-medium">{label}</span>
          </RadioGroupCard>
        ))}
      </RadioGroup>

      <div role="status" aria-relevant="all">
        {outcomeError && (
          <p id="contactStatus.error" className="tw:pt-3 tw:text-sm tw:font-light tw:text-red-500">
            {outcomeError.message}
          </p>
        )}
      </div>

      {callbackRequested && (
        <section
          aria-labelledby={callbackHeadingId}
          className="tw:mt-6 tw:rounded-lg tw:border tw:border-primary-200 tw:bg-primary-50/40 tw:p-5"
        >
          <h3 id={callbackHeadingId} className="tw:text-base tw:font-semibold tw:text-gray-900">
            {openCallback ? 'Reschedule the callback' : 'Schedule the callback'}
          </h3>
          <p className="tw:mb-4 tw:mt-1 tw:text-sm tw:text-gray-600">
            {openCallback
              ? existingCallbackNotice(SUPPORTER.firstName, openCallback, ownerName(openCallback), ME.id, now)
              : `${SUPPORTER.firstName} drops off this list until then. When it's due, they come back to the top.`}
          </p>
          <ScheduleCallbackFields
            draft={callback}
            onChange={setCallback}
            now={now}
            assign={
              canAssign
                ? { mode: 'admin', people: ASSIGNABLE, meId: ME.id, groupName: GROUP.name }
                : { mode: 'leader', meId: ME.id, meName: ME.name, groupName: GROUP.name }
            }
          />
        </section>
      )}

      {openCallback && !callbackRequested && (
        <div className="tw:mt-6">
          <CompleteCallbackField
            control={control}
            callback={openCallback}
            assigneeName={ownerName(openCallback)}
            now={now}
          />
        </div>
      )}

      {/* A callback is a further contact attempt, so this doesn't apply to one. */}
      {!callbackRequested && (
        <div className="tw:mt-4 tw:flex tw:flex-wrap tw:items-center">
          <Checkbox
            name="callCompleted"
            control={control}
            errors={errors}
            description="When checked, this person will be removed from future calling attempts in this session or list"
          >
            No further contact attempts needed
          </Checkbox>
        </div>
      )}

      {!callbackRequested && watch('callCompleted') && (
        <div className="tw:mt-4">
          <ContactOutcomeField control={control} errors={errors} />
        </div>
      )}

      <div className="tw:mt-6">
        <EventRsvpPicker events={SAMPLE_EVENTS} value={eventRsvpsField.value} onChange={eventRsvpsField.onChange} />
      </div>

      <div className="tw:mt-6">
        <Textarea label="Notes" errors={errors} rows={5} {...register('note')} />
      </div>

      <div className="tw:mt-4 tw:flex tw:justify-end">
        <Button type="submit" disabled={!!callbackError}>
          {callbackRequested
            ? openCallback
              ? 'Log call and move callback'
              : 'Log call and schedule callback'
            : 'Log call'}
        </Button>
      </div>
    </form>
  )
}

// ---------- Supporter summary card (#7415's SupporterSummaryCard, sample data) ----------

function SupporterSummaryCard({
  openCallback,
  held,
  now,
  onHeldChange,
}: {
  openCallback: CallbackDraft | null
  // Skipped in this session until the callback is done
  held: boolean
  now: Date
  onHeldChange: (held: boolean) => void
}) {
  const soon = (what: string) => () => toast.info(`Would open ${what}`)
  const rows = [
    { label: 'Last Contacted:', content: SUPPORTER.lastContacted },
    { label: 'Supporter Since:', content: SUPPORTER.supporterSince },
    { label: 'Location:', content: SUPPORTER.location },
    { label: 'Federal Electorate:', content: SUPPORTER.federalElectorate },
    { label: 'Background Note:', content: SUPPORTER.note, isNote: true },
  ]

  return (
    <div className="tw:mb-6 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-6">
      <div className="tw:mb-3 tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-baseline tw:gap-x-3 tw:gap-y-1">
          <h3 className="tw:m-0 tw:text-lg tw:font-semibold tw:text-gray-900">{SUPPORTER.fullName}</h3>
          <span className="tw:inline-flex tw:flex-wrap tw:gap-x-3 tw:gap-y-1 tw:text-sm tw:text-gray-500">
            <CopiableValue icon="phone-call-01" value={SUPPORTER.phone} copyLabel="Copy phone number" testId="phone" />
            <CopiableValue icon="mail-01" value={SUPPORTER.email} copyLabel="Copy email address" testId="email" />
          </span>
        </div>
        <span className="tw:min-w-fit">
          <SupporterStatusBadge
            status={held ? 'skipped_callback' : 'available'}
            unavailableReason={null}
            unavailableUntil={null}
            callbackDueAt={openCallback && callbackDueAt(openCallback).toISOString()}
            callbackFlexible={openCallback?.flexible}
          />
        </span>
      </div>

      <div className="tw:mb-3 tw:flex tw:flex-wrap tw:gap-1.5">
        {SUPPORTER.tags.map((tag) => (
          <Badge key={tag} color="gray" shape="rounded">
            {tag}
          </Badge>
        ))}
      </div>

      {openCallback && (
        <div className="tw:mb-4">
          <CallbackCard callback={openCallback} ownerName={ownerName(openCallback)} meId={ME.id} now={now}>
            <SkipInSessionSwitch firstName={SUPPORTER.firstName} checked={held} onCheckedChange={onHeldChange} />
          </CallbackCard>
        </div>
      )}

      <div className="tw:mb-6 tw:pt-2">
        <dl className="tw:grid tw:grid-cols-1 tw:items-center tw:gap-x-3 tw:gap-y-1 tw:sm:grid-cols-[max-content_1fr] tw:sm:gap-y-3">
          {rows.map(({ label, content, isNote }) => (
            <div key={label} className="tw:contents">
              <dt
                className={clsx(
                  'tw:flex tw:text-xs tw:font-bold tw:uppercase tw:text-gray-blue-500',
                  isNote ? 'tw:sm:items-start' : 'tw:items-center'
                )}
              >
                {label}
              </dt>
              <dd className="tw:m-0 tw:mb-3 tw:flex tw:text-sm tw:text-gray-900 tw:sm:mb-0">{content}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="tw:border-t tw:border-gray-200 tw:pt-6">
        <div className="tw:flex tw:flex-wrap tw:justify-between tw:gap-3">
          <div className="tw:flex tw:flex-wrap tw:gap-3">
            <Button rank="secondary" size="sm" leadingIcon="message-chat-square" onClick={soon('the SMS dialog')}>
              SMS {SUPPORTER.firstName}
            </Button>
            <Button rank="secondary" size="sm" leadingIcon="mail-01" onClick={soon('the email dialog')}>
              Email {SUPPORTER.firstName}
            </Button>
          </div>
          <div className="tw:flex tw:flex-wrap tw:gap-3">
            <Button rank="link" size="sm" leadingIcon="user-x" onClick={soon('contact preferences')}>
              Manage contact preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------- Page ----------

const sampleCallback = (now: Date): CallbackDraft => ({
  ...newCallbackDraft(now, 'u2', 'Wants to hear about the Saturday doorknock.'),
  flexible: true,
})

// The call where the open callback was booked, so the contacts log shows where it came from.
const initialContacts = (now: Date): ContactEntry[] => [
  {
    id: 'c0',
    outcome: 'callback_requested',
    at: at(-1, 13),
    by: 'Marco Ferri',
    note: 'Was heading into a meeting. Asked us to ring back after work.',
    callback: sampleCallback(now),
  },
  ...INITIAL_CONTACTS,
]

const CallingSessionV2CallbackPreview = () => {
  const [now] = useState(() => new Date())
  const [contacts, setContacts] = useState<ContactEntry[]>(() => initialContacts(now))
  const [openCallback, setOpenCallback] = useState<CallbackDraft | null>(() => sampleCallback(now))
  const [held, setHeld] = useState(true)
  // Remounts the form after a save, or when a design toggle changes its starting state.
  const [formKey, setFormKey] = useState(0)

  // Design tools
  const [showDesign, setShowDesign] = useState(false)
  const [role, setRole] = useState<'leader' | 'admin'>('leader')
  const [startWith, setStartWith] = useState<'nothing' | 'callback'>('nothing')

  const saveCall = ({ outcome, note, callback, completeCallback }: SavedCall) => {
    setContacts((all) => [
      {
        id: `c-new-${all.length + 1}`,
        outcome,
        at: new Date().toISOString(),
        by: ME.name,
        note,
        callback: callback ?? undefined,
      },
      ...all,
    ])
    if (callback) {
      setOpenCallback(callback)
      setHeld(true)
      toast.success(openCallback ? 'Call logged and callback moved' : 'Call logged and callback scheduled', {
        description: `${SUPPORTER.firstName} comes back to the top of the list ${draftWhen(callback, now)}.`,
      })
    } else if (completeCallback) {
      setOpenCallback(null)
      setHeld(false)
      toast.success('Call logged and callback marked complete')
    } else {
      toast.success('Call logged', {
        description: openCallback
          ? `${ownersCallback(callbackOwner(openCallback, ME.id), ownerName(openCallback))} for ${draftWhen(openCallback, now)} stays open.`
          : undefined,
      })
    }
    setFormKey((k) => k + 1)
  }

  const previous = SESSION_SUPPORTERS[POSITION - 2]
  const next = SESSION_SUPPORTERS[POSITION]

  return (
    <PageLayout.Root>
      <PageLayout.Header icon="user-square" heading={SUPPORTER.fullName} subtitle={`${SESSION.name} · ${GROUP.name}`} />
      <PageLayout.Body>
        {/* Today's CurrentCycleCard (from the main calling session supporter page), between cycles */}
        <div className="tw:mb-5 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-5">
          <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
            <Icon type="refresh-cw-04" className="tw:h-5 tw:w-5 tw:text-purple-600" />
            <h3 className="tw:text-base tw:font-semibold tw:text-gray-900">
              Cycle {CYCLE.number} - Today's calling cycle
            </h3>
          </div>
          <p className="tw:mb-4 tw:text-sm tw:text-gray-600">
            You have <span className="tw:font-semibold tw:text-primary-600">{CYCLE.available} supporters</span>{' '}
            available to call today.
          </p>
          <div className="tw:flex tw:items-center tw:gap-3">
            <Button
              size="md"
              leadingIcon="play-circle"
              onClick={() => toast.info('Would open the start calling cycle dialog')}
            >
              Start calling cycle
            </Button>
            <Button
              size="md"
              rank="secondary"
              leadingIcon="users-01"
              onClick={() =>
                toast.info('All 141 supporters, coming right up… one day 👋', {
                  description: 'This preview skips the full list, but they are all there, waiting by the phone.',
                })
              }
            >
              View all supporters
            </Button>
          </div>
        </div>

        {/* #7415's CallInProgressBanner */}
        <div className="tw:mb-5 tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-success-300 tw:bg-success-50/50 tw:px-4 tw:py-3">
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className="tw:h-2 tw:w-2 tw:shrink-0 tw:rounded-full tw:bg-success-500" />
            <span className="tw:text-sm tw:font-medium tw:text-gray-900">On a call with {SUPPORTER.fullName}</span>
          </div>
          <Button size="sm" trailingIcon="arrow-circle-broken-right" disabled>
            Currently viewing {SUPPORTER.fullName}
          </Button>
        </div>

        <div className="tw:mb-6 tw:grid tw:grid-cols-1 tw:gap-6 tw:lg:grid-cols-[minmax(0,1fr)_380px]">
          <LogCallForm
            key={`${formKey}-${role}`}
            now={now}
            canAssign={role === 'admin'}
            initialOutcome={startWith === 'callback' ? 'callback_requested' : ''}
            openCallback={openCallback}
            onSave={saveCall}
          />

          <aside className="tw:lg:sticky tw:lg:top-6 tw:lg:mt-12 tw:lg:self-start">
            <SupporterSummaryCard
              openCallback={openCallback}
              held={held}
              now={now}
              onHeldChange={(nextHeld) => {
                setHeld(nextHeld)
                toast.success(
                  nextHeld
                    ? `${SUPPORTER.fullName} won't be called until the callback is done`
                    : `${SUPPORTER.fullName} is back in the queue`
                )
              }}
            />
          </aside>
        </div>

        <Tabs defaultValue="contactsLog" urlKey="supporterTab" className="tw:flex tw:flex-col tw:gap-4">
          <TabsList className="tw:w-fit">
            <TabsTrigger value="supporterDetails">
              <Icon type="user-square" className="tw:mr-2" width={20} height={20} />
              Supporter details
            </TabsTrigger>
            <TabsTrigger value="contactsLog">
              <Icon type="mail-01" className="tw:mr-2" width={20} height={20} />
              Contacts log
            </TabsTrigger>
            <TabsTrigger value="notesLog">
              <Icon type="book-open-01" className="tw:mr-2" width={20} height={20} />
              Notes
            </TabsTrigger>
          </TabsList>
          <TabsContent value="supporterDetails">
            <p className="tw:text-sm tw:text-gray-500">The supporter details tab, unchanged from today.</p>
          </TabsContent>
          <TabsContent value="contactsLog">
            <ContactsLog contacts={contacts} ownerName={ownerName} meId={ME.id} now={now} />
          </TabsContent>
          <TabsContent value="notesLog">
            <p className="tw:text-sm tw:text-gray-500">The notes tab, unchanged from today.</p>
          </TabsContent>
        </Tabs>
      </PageLayout.Body>

      <PageLayout.Footer>
        {/* Stand-in for SupporterPagination */}
        <nav aria-label="Supporters in this session" className="tw:flex tw:w-full tw:items-center">
          <div className="tw:flex tw:flex-1 tw:justify-start">
            <Button
              rank="link"
              size="sm"
              leadingIcon="arrow-narrow-left"
              onClick={() => toast.info(`Would go to ${previous}`)}
            >
              Previous: {previous}
            </Button>
          </div>
          <ol className="tw:flex tw:items-center tw:justify-center tw:gap-4">
            {SESSION_SUPPORTERS.map((name, i) => (
              <li key={name}>
                <span
                  aria-current={i + 1 === POSITION ? 'page' : undefined}
                  title={name}
                  className={clsx(
                    'tw:inline-flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-full tw:text-xs tw:font-bold',
                    i + 1 === POSITION ? 'tw:bg-primary-700 tw:text-white' : 'tw:bg-gray-100 tw:text-black'
                  )}
                >
                  {i + 1}
                </span>
              </li>
            ))}
          </ol>
          <div className="tw:flex tw:flex-1 tw:justify-end">
            <Button
              rank="link"
              size="sm"
              trailingIcon="arrow-narrow-right"
              onClick={() => toast.info(`Would go to ${next}`)}
            >
              Next: {next}
            </Button>
          </div>
        </nav>
      </PageLayout.Footer>

      {/* Design tools: dev-only floating overlay, portaled to body, default closed */}
      {createPortal(
        <>
          <button
            onClick={() => setShowDesign((s) => !s)}
            className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
          >
            <Icon type="eye" className="tw:h-4 tw:w-4" />
            {showDesign ? 'Hide design tools' : 'Design tools'}
          </button>
          {showDesign && (
            <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
              <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                <button
                  onClick={() => setShowDesign(false)}
                  aria-label="Hide design tools"
                  className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
                >
                  <Icon type="x-close" className="tw:h-4 tw:w-4" />
                </button>
              </div>
              <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                This panel is purely for exploring the design and is not part of the product.
                <br />
                Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
              </p>
              <div className="tw:flex tw:flex-wrap tw:gap-8">
                <SegControl
                  label="Viewing as"
                  value={role}
                  onChange={(v) => setRole(v as typeof role)}
                  options={[
                    { value: 'leader', label: 'Leader' },
                    { value: 'admin', label: 'Admin' },
                  ]}
                />
                <SegControl
                  label="Form starts with"
                  value={startWith}
                  onChange={(v) => {
                    setStartWith(v as typeof startWith)
                    setFormKey((k) => k + 1)
                  }}
                  options={[
                    { value: 'nothing', label: 'Nothing picked' },
                    { value: 'callback', label: 'Callback requested' },
                  ]}
                />
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">
                    Sample data
                  </span>
                  <Button
                    rank="secondary"
                    size="sm"
                    leadingIcon="refresh"
                    onClick={() => {
                      setContacts(initialContacts(now))
                      setOpenCallback(sampleCallback(now))
                      setHeld(true)
                      setFormKey((k) => k + 1)
                    }}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>,
        document.body
      )}
    </PageLayout.Root>
  )
}

function SegControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value
                ? 'tw:bg-primary-50 tw:text-primary-700'
                : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default CallingSessionV2CallbackPreview
